import React from 'react';
import Link from 'next/link';
import BadgeTierChip, { BadgeTier } from './BadgeTierChip';
import VerificationBadge, { VerificationStatus } from './VerificationBadge';
import { Star } from 'lucide-react';

export interface ExpertCardData {
  id: string;
  pseudonym: string;
  avatarSeed: string;
  headline: string;
  domainTags: string[];
  badgeTier: BadgeTier;
  averageRating: number;
  ratingCount: number;
  hourlyRate: number;
  verificationStatus: VerificationStatus;
  experienceTier: string;
}

interface ExpertCardProps {
  expert: ExpertCardData;
  href?: string;
  showRequestButton?: boolean;
  onRequest?: () => void;
}

function AvatarPlaceholder({ seed, size = 64 }: { seed: string; size?: number }) {
  const colors = ['#f0b429', '#2dd4bf', '#a78bfa', '#fb7185', '#34d399'];
  const colorIndex = seed.charCodeAt(seed.length - 1) % colors.length;
  const initial = seed.replace('Advisor #', '').charAt(0);
  return (
    <div
      className="rounded-full flex items-center justify-center font-mono-data font-bold text-background flex-shrink-0"
      style={{ width: size, height: size, background: colors[colorIndex], fontSize: size * 0.35 }}
      aria-label={`Avatar for ${seed}`}
      role="img"
    >
      {initial}
    </div>
  );
}

function StarRating({ value, count }: { value: number; count: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={`star-${star}`}
            size={12}
            className={star <= Math.round(value) ? 'text-primary fill-primary' : 'text-border'}
            fill={star <= Math.round(value) ? 'currentColor' : 'none'}
          />
        ))}
      </div>
      <span className="font-mono-data text-xs text-foreground">{value.toFixed(1)}</span>
      <span className="text-xs text-muted-foreground">({count})</span>
    </div>
  );
}

export default function ExpertCard({ expert, href, showRequestButton, onRequest }: ExpertCardProps) {
  const cardContent = (
    <div className="surface-elevated card-hover p-5 flex flex-col gap-4 h-full group">
      <div className="flex items-start gap-3">
        <AvatarPlaceholder seed={expert.avatarSeed} size={52} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-mono-data text-sm font-semibold text-foreground">{expert.pseudonym}</span>
            <BadgeTierChip tier={expert.badgeTier} size="sm" />
          </div>
          <p className="text-xs text-muted-foreground font-mono-data">{expert.experienceTier}</p>
        </div>
        <VerificationBadge status={expert.verificationStatus} size="sm" />
      </div>

      <p className="text-sm text-foreground leading-relaxed line-clamp-2 flex-1">
        {expert.headline}
      </p>

      <div className="flex flex-wrap gap-1.5">
        {expert.domainTags.slice(0, 4).map((tag) => (
          <span
            key={`tag-${expert.id}-${tag}`}
            className="text-[10px] font-mono-data px-2 py-0.5 rounded-full bg-primary/8 text-primary border border-primary/15"
          >
            {tag}
          </span>
        ))}
        {expert.domainTags.length > 4 && (
          <span className="text-[10px] font-mono-data px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
            +{expert.domainTags.length - 4}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-border">
        <StarRating value={expert.averageRating} count={expert.ratingCount} />
        <div className="text-right">
          <span className="font-mono-data text-sm font-semibold text-foreground">
            ₹{expert.hourlyRate.toLocaleString('en-IN')}
          </span>
          <span className="text-xs text-muted-foreground">/hr</span>
        </div>
      </div>

      {showRequestButton && (
        <button
          onClick={(e) => { e.preventDefault(); onRequest?.(); }}
          className="w-full mt-1 py-2 px-4 rounded bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all duration-150"
        >
          Request This Expert
        </button>
      )}
    </div>
  );

  if (href) {
    return (
      <Link href={href} className="block h-full" aria-label={`View profile for ${expert.pseudonym}`}>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}